import { P2GButton, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { convertMetersToMiles } from '~/lib/distance';
import { toDisplayCurrency } from '~/lib/prices';
import { BasketQuote } from '~/lib/types/basket-types';
import { Shop } from '~/lib/types/edge-api-types';
import P2GOptionContainer from '../option/option';
import { useEffect, useMemo, useState } from 'react';
import QuoteCardServiceLogo from '~/components/quote-results/card/quote-card-logo';
import { useDistance, useTaxName } from '@parcel2go/npm-p2g-app';

export default function ServiceUpgradeOption({
  newQuote,
  originalQuote,
  newShops,
  originalShops,
  onSelect
}: {
  newQuote: BasketQuote;
  originalQuote: BasketQuote;
  newShops?: Shop[];
  originalShops?: Shop[];
  onSelect: (quote: BasketQuote) => void;
}) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const [isDelayed, setIsDelayed] = useState(false);
  const { toDistance } = useDistance();
  const taxName = useTaxName();

  const isSwitchToDropOff =
    !originalQuote.tags.some((tag) => tag === 'dropoff') && newQuote.tags.some((tag) => tag === 'dropoff');
  const newDeliveryDate = new Date(newQuote.dates[0]?.delivery?.dateMax);
  const originalDeliveryDate = new Date(originalQuote.dates[0]?.delivery?.dateMax);
  const daysDifference = Math.floor(
    (originalDeliveryDate.getTime() - newDeliveryDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const originalDistance = (originalShops || [])[0]?.distance;
  const newDistance = (newShops || [])[0]?.distance;
  const distanceDifference = originalDistance && newDistance && convertMetersToMiles(originalDistance - newDistance);
  const currencyDisplay = toDisplayCurrency(newQuote.currency, newQuote.price.net - originalQuote.price.net);
  const isQuicker = newDeliveryDate.getTime() < originalDeliveryDate.getTime();
  const isCloser = originalDistance && newDistance && newDistance < originalDistance;
  const isSameCourier = newQuote.service.courier === originalQuote.service.courier;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDelayed(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const header = useMemo(() => {
    if (isCloser) {
      return (
        <Trans
          i18nKey="SERVICE_UPGRADE_TITLE_CLOSER"
          t={t}
          defaults="Need a Closer Drop Off?"
          values={{ courier: newQuote.service.courier, service: newQuote.service.name }}
        />
      );
    }
    if (isQuicker) {
      return (
        <Trans
          i18nKey="SERVICE_UPGRADE_TITLE_NEED_IT_QUICKER"
          t={t}
          defaults="Need a Quicker Service?"
          values={{ courier: newQuote.service.courier, service: newQuote.service.name }}
        />
      );
    }
    return <>{newQuote.service.name}</>;
  }, [t, isCloser, isQuicker, newQuote.service]);

  const content = useMemo(() => {
    if (isCloser && isQuicker) {
      return (
        <Trans
          i18nKey="SERVICE_UPGRADE_CLOSER_QUICKER"
          t={t}
          defaults="{{courier}} offers a drop-off location that’s {{distanceDifference}} closer and arriving {{difference}} day(s) sooner for just {{price}} (exc {{taxName}}) extra."
          values={{
            distanceDifference: toDistance(distanceDifference),
            difference: daysDifference,
            distance: toDistance(newDistance),
            price: currencyDisplay,
            courier: newQuote.service.courier,
            service: newQuote.service.name,
            taxName: taxName
          }}
          components={[<P2GText fontWeight="bold" />]}
        />
      );
    }

    if (isCloser) {
      return (
        <Trans
          i18nKey="SERVICE_UPGRADE_CLOSER"
          t={t}
          defaults="<0>{{courier}}<0/> offers a drop-off location that’s {{distanceDifference}} closer for just {{price}} (exc {{taxName}}) extra."
          values={{
            distanceDifference: toDistance(distanceDifference),
            distance: toDistance(newDistance),
            price: currencyDisplay,
            courier: newQuote.service.courier,
            service: newQuote.service.name,
            taxName: taxName
          }}
          components={[<P2GText fontWeight="bold" />]}
        />
      );
    }

    if (isQuicker) {
      return (
        <Trans
          i18nKey="SERVICE_UPGRADE_QUICKER"
          t={t}
          defaults={
            isSameCourier
              ? 'Upgrade to <0>{{service}}</0> and enjoy faster delivery, arriving {{difference}} day(s) sooner, for just {{price}} (exc {{taxName}}) extra.'
              : 'Upgrade to <0>{{courier}}</0> and enjoy faster delivery, arriving {{difference}} day(s) sooner, for just {{price}} (exc {{taxName}}) extra.'
          }
          values={{
            difference: daysDifference,
            price: currencyDisplay,
            courier: newQuote.service.courier,
            service: newQuote.service.name,
            taxName: taxName
          }}
          components={[<P2GText fontWeight="bold" />]}
        />
      );
    }

    if (isSwitchToDropOff) {
      return (
        <Trans
          i18nKey="SERVICE_UPGRADE_TO_DROP_OFF"
          t={t}
          defaults="There is a drop off within {{distance}} if you don't want to wait in for a collection."
          values={{
            distance: toDistance(newDistance),
            price: currencyDisplay,
            courier: newQuote.service.courier,
            service: newQuote.service.name
          }}
          components={[<P2GText fontWeight="bold" />]}
        />
      );
    }
    return null;
  }, [
    t,
    isCloser,
    isQuicker,
    isSwitchToDropOff,
    newQuote.service,
    currencyDisplay,
    daysDifference,
    distanceDifference,
    newDistance,
    isSameCourier,
    taxName,
    toDistance
  ]);

  return (
    <P2GOptionContainer variant="upgrade" dataTestId="service-upgrade">
      <P2GGrid container spacing={2} alignContent="center" sx={{ alignItems: { lg: 'center' } }}>
        <P2GGrid item flexShrink={1} sm={12} md={'auto'}>
          <QuoteCardServiceLogo
            slug={newQuote.service.slug}
            width="140px"
            minWidth="140px"
            dataTestId="service-upgrade-logo"
          />
        </P2GGrid>
        <P2GGrid item flexGrow={1} sx={{ width: { xs: '100%', lg: null } }}>
          <P2GGrid container direction="column">
            <P2GGrid item>
              <P2GText fontWeight="bold" dataTestId="service-upgrade-title">
                {header}
              </P2GText>
            </P2GGrid>
            <P2GGrid item dataTestId="service-upgrade-content">
              {content}
            </P2GGrid>
          </P2GGrid>
        </P2GGrid>
        <P2GGrid item flexShrink={1} xs={12} lg={'auto'}>
          <P2GButton
            variant="secondary"
            size="small"
            onClick={() => (isDelayed ? onSelect(newQuote) : () => {})}
            dataTestId="service-upgrade-button"
          >
            {isSwitchToDropOff
              ? t('SERVICE_CHANGE_BUTTON', {
                  defaultValue: 'Change'
                })
              : t('SERVICE_UPGRADE_BUTTON', {
                  defaultValue: 'Upgrade'
                })}
          </P2GButton>
        </P2GGrid>
      </P2GGrid>
    </P2GOptionContainer>
  );
}
